/**
 * Sweetbox Add-ons Popup
 * Handles the popup functionality for adding product add-ons to gift boxes.
 */
class SweetboxPopup {
  constructor() {
    // DOM Elements
    this.popup = document.getElementById('sweetboxPopup');
    if (!this.popup) return; // Popup not on this page

    this.popupContainer = this.popup.querySelector('.sweetbox-popup-container');
    this.closeBtn = this.popup.querySelector('[data-close-popup]');
    this.categoriesScreen = document.getElementById('sweetboxCategoriesScreen');
    this.productsScreen = document.getElementById('sweetboxProductsScreen');
    this.categoriesGrid = document.getElementById('sweetboxCategoriesGrid');
    this.productsGrid = document.getElementById('sweetboxProductsGrid');
    this.categoryTitle = this.popup.querySelector('.category-title');
    this.backBtn = document.getElementById('sweetboxBackBtn');
    this.loadingElement = document.getElementById('sweetboxLoading');
    this.successMessage = document.getElementById('sweetboxSuccessMessage');
    this.successText = document.getElementById('sweetboxSuccessText');

    this.categoriesData = [];
    this.currentProduct = null;

    this.lastTriggerBtn = null;

    this.init();
  }

  /**
   * Initialize the popup
   */
  init() {
    if (!this.popup) return;

    // Load categories data
    this.loadCategoriesData();

    // Add event listeners
    this.setupEventListeners();
  }

  /**
   * Load categories data from hidden JSON script
   */
  loadCategoriesData() {
    // Initialize with empty array as fallback
    this.categoriesData = [];

    const dataElement = document.getElementById('sweetboxCategoriesData');
    if (!dataElement) {
      console.error('Sweetbox data element not found. Make sure the popup section is included correctly.');
      return;
    }

    try {
      const data = JSON.parse(dataElement.textContent);

      // Validate data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid data structure: expected object');
      }

      if (!Array.isArray(data.categories)) {
        throw new Error('Invalid data structure: categories should be an array');
      }

      this.categoriesData = data.categories || [];
      this.currentProduct = data.currentProduct || null;

      console.log('Sweetbox categories loaded:', this.categoriesData.length);
    } catch (error) {
      console.error('Error parsing Sweetbox categories data:', error);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Open popup (supports multiple triggers anywhere on the page)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.sweetbox-trigger-btn');
      if (!trigger) return;
      e.preventDefault();
      this.openPopup(trigger);
    });

    // Close popup
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closePopup());
    }

    // Click outside to close - only if clicking directly on overlay background
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.closePopup();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('is-open')) {
        this.closePopup();
      }
    });

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.showCategories());
    }

    // Category cards click (event delegation)
    if (this.categoriesGrid) {
      this.categoriesGrid.addEventListener('click', (e) => {
        const categoryCard = e.target.closest('.sweetbox-category-card');
        if (!categoryCard) return;

        e.preventDefault();
        e.stopPropagation();

        let categoryId = categoryCard.getAttribute('data-category-id') || categoryCard.dataset.categoryId;

        if (categoryId) {
          this.showProducts(categoryId);
        } else {
          console.error('Category ID not found on card:', categoryCard);
        }
      });

      // Keyboard accessibility for category cards
      this.categoriesGrid.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;

        const categoryCard = e.target.closest('.sweetbox-category-card');
        if (!categoryCard) return;

        e.preventDefault();
        const categoryId = categoryCard.getAttribute('data-category-id') || categoryCard.dataset.categoryId;
        if (categoryId) this.showProducts(categoryId);
      });
    }

    // Add to cart buttons (event delegation)
    if (this.productsGrid) {
      this.productsGrid.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-add-to-cart]');
        if (!addBtn) return;

        e.preventDefault();
        const variantId = addBtn.getAttribute('data-variant-id');
        const productTitle = addBtn.getAttribute('data-product-title');
        if (!variantId) return;

        this.addProductToCart(variantId, productTitle);
      });
    }
  }

  openPopup(triggerEl) {
    this.lastTriggerBtn = triggerEl || this.lastTriggerBtn;

    this.popup.classList.add('is-open');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Show categories by default
    this.showCategories();

    // Focus on close button for accessibility
    setTimeout(() => {
      if (this.closeBtn) this.closeBtn.focus();
    }, 100);
  }

  closePopup() {
    this.popup.classList.remove('is-open');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Reset to categories view
    this.showCategories();

    // Return focus
    if (this.lastTriggerBtn) this.lastTriggerBtn.focus();
  }

  /**
   * Show categories screen
   */
  showCategories() {
    if (this.categoriesScreen) this.categoriesScreen.style.display = 'block';
    if (this.productsScreen) this.productsScreen.style.display = 'none';
  }

  /**
   * Show products for a selected category
   */
  showProducts(categoryId) {
    if (!this.categoriesData || !this.categoriesData.length) return;

    const category = this.categoriesData.find(cat => String(cat.id) === String(categoryId));
    if (!category) {
      console.error('Category not found for ID:', categoryId);
      return;
    }

    if (this.categoriesScreen) this.categoriesScreen.style.display = 'none';
    if (this.productsScreen) this.productsScreen.style.display = 'block';

    // Update category title
    if (this.categoryTitle && category.title) {
      this.categoryTitle.textContent = category.title;
    }

    this.renderProducts(category);
  }

  /**
   * Render products grid for selected category
   */
  renderProducts(category) {
    if (!this.productsGrid) return;

    const products = Array.isArray(category.products) ? category.products : [];
    if (!products.length) {
      this.productsGrid.classList.add('empty');
      this.productsGrid.innerHTML = '<div class="sweetbox-empty-message">אין מוצרים זמינים בקטגוריה זו</div>';
      return;
    }

    // clean empty state
    this.productsGrid.classList.remove('empty');

    this.productsGrid.innerHTML = products.map(p => {
      const price = this.formatPrice(p.price);
      const compareAt = p.compareAtPrice ? this.formatPrice(p.compareAtPrice) : null;

      return `
        <div class="sweetbox-product-card">
        <a href="${p.url}" class="sweetbox-product-link" target="_blank">
          <div class="sweetbox-product-img-wrap">
            ${p.image ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy" />` : ''}
          </div>
          <h4 class="sweetbox-product-title">${this.escapeHtml(p.title)}</h4>
        </a>

          <div class="sweetbox-product-info">
            <div class="sweetbox-product-prices">
              <span class="sweetbox-product-price">${price}</span>
              ${compareAt ? `<span class="sweetbox-product-compare">${compareAt}</span>` : ''}
            </div>

            <button
              type="button"
              class="sweetbox-add-to-cart-btn"
              data-add-to-cart
              data-variant-id="${this.escapeHtml(String(p.variantId))}"
              data-product-title="${this.escapeHtml(p.title)}"
              aria-label="Add ${this.escapeHtml(p.title)} to cart"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  async addProductToCart(variantId, productTitle) {
    try {
      this.showLoading(true);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                box: this.currentProduct?.title || '',
                _sweetbox_box_product: this.currentProduct?.title || '',
                _sweetbox_box_sku: this.currentProduct?.sku || ''
              }
            }
          ]
        })

      });

      if (!res.ok) throw new Error('Failed to add to cart');

      this.showSuccessMessage(productTitle);
    } catch (err) {
      console.error(err);
      alert('משהו השתבש בהוספה לעגלה');
    } finally {
      this.showLoading(false);
    }
  }

  showSuccessMessage(productTitle) {
    if (!this.successMessage || !this.successText) return;

    this.successText.textContent = `נוסף לעגלה: ${productTitle || ''}`;
    this.successMessage.style.display = 'flex';

    setTimeout(() => {
      this.successMessage.style.display = 'none';
    }, 2000);
  }

  showLoading(isLoading) {
    if (!this.loadingElement) return;
    this.loadingElement.style.display = isLoading ? 'flex' : 'none';
  }

  formatPrice(cents) {
    if (typeof cents !== 'number') return '';
    const value = (cents / 100).toFixed(2);
    return `₪${value}`;
  }

  escapeHtml(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SweetboxPopup();
});